/**
 * Data Import admin page — centralized upload area for all manual data types.
 *
 * Features:
 * - Upload buttons organized by recurring vs. one-time sections
 * - RBAC: Admin sees all; Controller/CFO see only Branch Ranking
 * - File upload → preview (total rows + first 10 rows) → confirm/cancel
 * - Progress indicator during import
 * - Results summary with error details
 * - Import history table
 *
 * Requirements: 48.1–48.3, 48.11–48.13
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  dataImportService,
  type ImportType,
  type ImportPreview,
  type ImportResult,
  type ImportHistoryEntry,
} from '@/services/dataImportService';

// ─── Sub-components ────────────────────────────────────────────────────

/** Upload button for a single import type. */
function ImportUploadButton({
  importType,
  onFileSelected,
  disabled,
}: {
  importType: ImportType;
  onFileSelected: (file: File, importType: ImportType) => void;
  disabled: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClick = () => {
    inputRef.current?.click();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onFileSelected(file, importType);
      e.target.value = '';
    }
  };

  return (
    <div className="mb-3">
      <button
        onClick={handleClick}
        disabled={disabled}
        className="w-full text-left bg-white rounded-lg border border-border p-4 hover:border-baker-accent hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        aria-label={`Upload ${importType.label}`}
      >
        <div className="font-medium text-text-primary">{importType.label}</div>
        <div className="text-sm text-slate-500 mt-1">{importType.description}</div>
        <div className="text-xs text-slate-400 mt-1">Accepted: {importType.acceptedFormats}</div>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={importType.acceptedFormats}
        onChange={handleChange}
        className="hidden"
        aria-hidden="true"
      />
    </div>
  );
}

/** Preview table showing sample rows. */
function PreviewTable({ preview }: { preview: ImportPreview }) {
  if (preview.sampleRows.length === 0) {
    return <p className="text-slate-400">No data rows found in the file.</p>;
  }

  return (
    <div className="overflow-x-auto max-h-96 rounded-lg border border-border">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-page border-b border-border">
            {preview.headers.map((header) => (
              <th
                key={header}
                className="py-2.5 px-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500 whitespace-nowrap"
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {preview.sampleRows.map((row, idx) => (
            <tr key={idx} className="border-b border-border hover:bg-page/50">
              {preview.headers.map((header) => (
                <td key={header} className="py-2 px-3 whitespace-nowrap text-slate-700">
                  {row[header] != null ? String(row[header]) : ''}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Results summary after import. */
function ResultsSummary({ result }: { result: ImportResult }) {
  return (
    <div className="bg-white rounded-lg border border-border p-5 mt-5">
      <h3 className="text-lg font-semibold text-text-primary mb-3">Import Results</h3>
      <div className="flex flex-wrap gap-4 mb-3">
        <div className="bg-page rounded-lg px-4 py-2 text-center">
          <div className="text-lg font-semibold text-text-primary">{result.totalProcessed}</div>
          <div className="text-xs text-slate-500">Total Processed</div>
        </div>
        <div className="bg-green-50 rounded-lg px-4 py-2 text-center">
          <div className="text-lg font-semibold text-green-700">{result.created}</div>
          <div className="text-xs text-slate-500">Created</div>
        </div>
        <div className="bg-blue-50 rounded-lg px-4 py-2 text-center">
          <div className="text-lg font-semibold text-blue-700">{result.updated}</div>
          <div className="text-xs text-slate-500">Updated</div>
        </div>
        <div className="bg-red-50 rounded-lg px-4 py-2 text-center">
          <div className="text-lg font-semibold text-red-700">{result.errors.length}</div>
          <div className="text-xs text-slate-500">Errors</div>
        </div>
      </div>
      {result.errors.length > 0 && (
        <details>
          <summary className="cursor-pointer text-red-600 text-sm font-medium">
            View {result.errors.length} error(s)
          </summary>
          <ul className="max-h-48 overflow-y-auto text-sm mt-2 space-y-1">
            {result.errors.map((err, idx) => (
              <li key={idx} className="text-red-600">
                {err.row != null && <span className="font-medium">Row {err.row}: </span>}
                {err.error}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

/** Import history table. */
function HistoryTable({ history, importTypes }: { history: ImportHistoryEntry[]; importTypes: ImportType[] }) {
  if (history.length === 0) {
    return <p className="text-slate-400">No import history found.</p>;
  }

  const getLabel = (id: string) => importTypes.find((t) => t.id === id)?.label ?? id;

  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-page border-b border-border">
            <th className="py-2.5 px-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Type</th>
            <th className="py-2.5 px-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">File</th>
            <th className="py-2.5 px-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Imported By</th>
            <th className="py-2.5 px-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Date</th>
            <th className="py-2.5 px-3 text-right text-xs font-medium uppercase tracking-wide text-slate-500">Created</th>
            <th className="py-2.5 px-3 text-right text-xs font-medium uppercase tracking-wide text-slate-500">Updated</th>
            <th className="py-2.5 px-3 text-right text-xs font-medium uppercase tracking-wide text-slate-500">Errors</th>
          </tr>
        </thead>
        <tbody>
          {history.map((entry, idx) => (
            <tr key={idx} className="border-b border-border hover:bg-page/50">
              <td className="py-2 px-3">{getLabel(entry.importType)}</td>
              <td className="py-2 px-3">{entry.fileName}</td>
              <td className="py-2 px-3">{entry.importedBy}</td>
              <td className="py-2 px-3">{new Date(entry.importedAt).toLocaleString()}</td>
              <td className="py-2 px-3 text-right">{entry.created}</td>
              <td className="py-2 px-3 text-right">{entry.updated}</td>
              <td className="py-2 px-3 text-right">{entry.errors}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────

type ImportStep = 'idle' | 'previewing' | 'preview' | 'processing' | 'done';

export function DataImport() {
  const { user } = useAuth();

  const [importTypes, setImportTypes] = useState<ImportType[]>([]);
  const [history, setHistory] = useState<ImportHistoryEntry[]>([]);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Import workflow state
  const [step, setStep] = useState<ImportStep>('idle');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedType, setSelectedType] = useState<ImportType | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);

  useEffect(() => {
    loadImportTypes();
    loadHistory();
  }, []);

  const loadImportTypes = async () => {
    try {
      setLoadingTypes(true);
      const types = await dataImportService.getImportTypes();
      setImportTypes(types);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load import types.');
    } finally {
      setLoadingTypes(false);
    }
  };

  const loadHistory = async () => {
    try {
      setLoadingHistory(true);
      const h = await dataImportService.getImportHistory();
      setHistory(h);
    } catch {
      // Non-critical — history may be empty
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleFileSelected = useCallback(async (file: File, importType: ImportType) => {
    setError(null);
    setResult(null);
    setSelectedFile(file);
    setSelectedType(importType);
    setStep('previewing');

    try {
      const previewData = await dataImportService.previewImport(file, importType.id);
      setPreview(previewData);
      setStep('preview');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to preview file.');
      setStep('idle');
    }
  }, []);

  const handleConfirmImport = useCallback(async () => {
    if (!selectedFile || !selectedType) return;

    setError(null);
    setStep('processing');

    try {
      const importResult = await dataImportService.processImport(selectedFile, selectedType.id);
      setResult(importResult);
      setStep('done');
      loadHistory();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed.');
      setStep('preview');
    }
  }, [selectedFile, selectedType]);

  const handleCancel = useCallback(() => {
    setStep('idle');
    setSelectedFile(null);
    setSelectedType(null);
    setPreview(null);
    setResult(null);
    setError(null);
  }, []);

  const handleNewImport = useCallback(() => {
    setStep('idle');
    setSelectedFile(null);
    setSelectedType(null);
    setPreview(null);
    setResult(null);
    setError(null);
  }, []);

  const recurringTypes = importTypes.filter((t) => t.isRecurring);
  const oneTimeTypes = importTypes.filter((t) => !t.isRecurring);
  const isProcessing = step === 'previewing' || step === 'processing';

  if (!user) return null;

  return (
    <div className=" max-w-6xl">
      <h1 className="text-lg font-semibold text-text-primary mb-2">Data Import</h1>
      <p className="text-sm text-slate-500 mb-6">Upload and import data files for various system data types.</p>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-6 py-4 text-red-700 text-sm mb-5" role="alert">
          {error}
        </div>
      )}

      {/* Upload Buttons Section */}
      {step === 'idle' && (
        <>
          {loadingTypes ? (
            <p className="text-slate-400">Loading import types...</p>
          ) : (
            <>
              {recurringTypes.length > 0 && (
                <section className="mb-8">
                  <h2 className="text-lg font-semibold text-text-primary mb-1">Recurring Imports</h2>
                  <p className="text-sm text-slate-500 mb-4">
                    Data that is imported on a regular schedule (weekly, annually).
                  </p>
                  {recurringTypes.map((t) => (
                    <ImportUploadButton
                      key={t.id}
                      importType={t}
                      onFileSelected={handleFileSelected}
                      disabled={isProcessing}
                    />
                  ))}
                </section>
              )}

              {oneTimeTypes.length > 0 && (
                <section className="mb-8">
                  <h2 className="text-lg font-semibold text-text-primary mb-1">One-Time Seed Imports</h2>
                  <p className="text-sm text-slate-500 mb-4">
                    Initial data loads for system setup. Can be re-run to update existing records.
                  </p>
                  {oneTimeTypes.map((t) => (
                    <ImportUploadButton
                      key={t.id}
                      importType={t}
                      onFileSelected={handleFileSelected}
                      disabled={isProcessing}
                    />
                  ))}
                </section>
              )}
            </>
          )}
        </>
      )}

      {/* Preview Step */}
      {step === 'previewing' && (
        <div className="text-center py-10">
          <p className="text-slate-500">Loading preview...</p>
        </div>
      )}

      {step === 'preview' && preview && selectedType && selectedFile && (
        <section>
          <h2 className="text-lg font-semibold text-text-primary mb-2">Preview: {selectedType.label}</h2>
          <p className="text-sm text-slate-500 mb-4">
            File: <span className="font-medium text-text-primary">{selectedFile.name}</span> — {preview.totalRows} row(s) detected
          </p>

          <PreviewTable preview={preview} />

          <div className="mt-5 flex gap-3">
            <button
              onClick={handleConfirmImport}
              className="px-4 py-2 rounded-lg bg-baker-red text-white text-sm font-medium hover:bg-baker-red/90"
            >
              Confirm Import
            </button>
            <button
              onClick={handleCancel}
              className="px-4 py-2 rounded-lg border border-border bg-white text-sm font-medium text-slate-600 hover:bg-page"
            >
              Cancel
            </button>
          </div>
        </section>
      )}

      {/* Processing Step */}
      {step === 'processing' && (
        <div className="text-center py-10">
          <p className="text-slate-500 mb-4">Processing import... Please wait.</p>
          <div className="w-48 h-1 bg-baker-border rounded-full mx-auto overflow-hidden">
            <div className="h-full w-3/5 bg-baker-accent rounded-full animate-pulse" />
          </div>
        </div>
      )}

      {/* Results Step */}
      {step === 'done' && result && (
        <section>
          <h2 className="text-lg font-semibold text-text-primary mb-2">Import Complete</h2>
          <ResultsSummary result={result} />
          <div className="mt-5">
            <button
              onClick={handleNewImport}
              className="px-4 py-2 rounded-lg bg-baker-red text-white text-sm font-medium hover:bg-baker-red/90"
            >
              Start New Import
            </button>
          </div>
        </section>
      )}

      {/* Import History */}
      <section className="mt-12">
        <h2 className="text-lg font-semibold text-text-primary mb-4">Import History</h2>
        {loadingHistory ? (
          <p className="text-slate-400">Loading history...</p>
        ) : (
          <HistoryTable history={history} importTypes={importTypes} />
        )}
      </section>
    </div>
  );
}
